import React, { useEffect, useRef } from 'react';

/**
 * TrailMagnifier
 * --------------
 * Comet-trail magnifying lens. When the mouse moves, N soft-edged circular
 * lenses follow the cursor with per-lens lerp inertia. Each lens shows the
 * hero content underneath ENLARGED (~1.22x) — nothing is hidden, no reveal
 * layer swap, no diagonal cut. Base content is fully visible always;
 * lenses only zoom what's already there.
 *
 * Behaviour (verbatim from the brand brief):
 *   - At rest: invisible (global alpha 0).
 *   - Moving: a chain of lenses trails the cursor; head crisp, tail
 *     progressively more transparent + blurred (comet smear).
 *   - Faster movement → longer / more marked trail.
 *   - Stopping → alpha eases back to 0.
 *   - Disabled on touch (`hover: none`) and prefers-reduced-motion.
 *
 * Magnify math (locks the pixel under each lens to its center):
 *   inner.transform = translate(R - x*zoom, R - y*zoom) scale(zoom)
 *   transform-origin: 0 0
 *
 * Performance: each lens clones `children` at natural viewport size. Kept
 * at count=5 by default (spec allows 4–6). If frame drops, lower `count`.
 */
export default function TrailMagnifier({
    children,
    count = 5,
    radius = 118,
    zoom = 1.22,
}) {
    const wrap = useRef(null);
    const layer = useRef(null);
    const lensRefs = useRef([]);
    const innerRefs = useRef([]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.matchMedia('(hover: none)').matches) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const pts = Array.from({ length: count }, () => ({
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
        }));
        let mx = window.innerWidth / 2;
        let my = window.innerHeight / 2;
        let alpha = 0;
        let px = mx;
        let py = my;
        let raf = 0;
        // Text-lens focus: track the element currently highlighted by the
        // lens so we can remove the class when the lens moves off. Checked
        // every ~6 frames (~100ms @ 60fps) to keep the rAF loop cheap.
        let focusEl = null;
        let frameTick = 0;
        const FOCUS_SELECTOR = '.side-title, .side-menu-link';
        const FOCUS_CLASS = 'text-lens-focus';
        const clearFocus = () => {
            if (focusEl) {
                focusEl.classList.remove(FOCUS_CLASS);
                focusEl = null;
            }
        };

        const onMove = (e) => {
            mx = e.clientX;
            my = e.clientY;
        };

        const wrapEl = wrap.current;
        if (!wrapEl) return;
        wrapEl.addEventListener('mousemove', onMove);

        const loop = () => {
            // Head follows cursor with lerp 0.35
            pts[0].x += (mx - pts[0].x) * 0.35;
            pts[0].y += (my - pts[0].y) * 0.35;
            // Each subsequent lens follows the previous one with lerp 0.35
            for (let i = 1; i < count; i++) {
                pts[i].x += (pts[i - 1].x - pts[i].x) * 0.35;
                pts[i].y += (pts[i - 1].y - pts[i].y) * 0.35;
            }
            // Global alpha driven by head speed
            const speed = Math.hypot(pts[0].x - px, pts[0].y - py);
            px = pts[0].x;
            py = pts[0].y;
            alpha += (Math.min(speed / 7, 1) - alpha) * 0.12;
            if (layer.current) {
                layer.current.style.opacity = String(alpha);
            }
            // Text-lens focus: only run detection when the lens is
            // visible enough (α > 0.15) and every 6th frame to avoid
            // per-frame `elementsFromPoint` cost. The lens layer itself
            // has pointer-events:none so it is skipped by hit testing.
            frameTick++;
            if (alpha > 0.15 && frameTick % 6 === 0) {
                const hits = document.elementsFromPoint(pts[0].x, pts[0].y);
                let next = null;
                for (const el of hits) {
                    if (el && el.matches && el.matches(FOCUS_SELECTOR)) {
                        next = el;
                        break;
                    }
                }
                if (next !== focusEl) {
                    if (focusEl) focusEl.classList.remove(FOCUS_CLASS);
                    if (next) next.classList.add(FOCUS_CLASS);
                    focusEl = next;
                }
            } else if (alpha <= 0.05 && focusEl) {
                clearFocus();
            }
            for (let i = 0; i < count; i++) {
                const p = pts[i];
                const lensEl = lensRefs.current[i];
                const innerEl = innerRefs.current[i];
                if (lensEl) {
                    lensEl.style.transform = `translate(${p.x}px, ${p.y}px)`;
                }
                if (innerEl) {
                    innerEl.style.transform =
                        `translate(${radius - p.x * zoom}px, ${radius - p.y * zoom}px) scale(${zoom})`;
                }
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(raf);
            wrapEl.removeEventListener('mousemove', onMove);
            clearFocus();
        };
    }, [count, radius, zoom]);

    const mask =
        'radial-gradient(circle at center, #000 55%, rgba(0,0,0,0.55) 80%, transparent 100%)';

    return (
        <div
            ref={wrap}
            className="trail-magnifier-wrap"
            data-testid="trail-magnifier-wrap"
        >
            {children}
            <div
                ref={layer}
                aria-hidden="true"
                className="trail-magnifier-layer"
                data-testid="trail-magnifier-layer"
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 5,
                    pointerEvents: 'none',
                    opacity: 0,
                }}
            >
                {Array.from({ length: count }).map((_, i) => (
                    <div
                        key={i}
                        ref={(el) => {
                            if (el) lensRefs.current[i] = el;
                        }}
                        className="trail-magnifier-lens"
                        data-testid={`trail-magnifier-lens-${i}`}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: radius * 2,
                            height: radius * 2,
                            margin: `${-radius}px 0 0 ${-radius}px`,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            opacity: 1 - (i / count) * 0.78,
                            filter: `blur(${i * 0.9}px)`,
                            WebkitMaskImage: mask,
                            maskImage: mask,
                            willChange: 'transform, filter, opacity',
                        }}
                    >
                        <div
                            ref={(el) => {
                                if (el) innerRefs.current[i] = el;
                            }}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                transformOrigin: '0 0',
                                willChange: 'transform',
                            }}
                        >
                            {children}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
